import { formatISODateInTimezone, getHourInTimezone } from './formatEventDateTime';

// Marketplace date/time filtering, done in the venue's zone rather than the viewer's.

/** Shape shared by `lessons` and `events` rows. */
export interface VenueTimedEntity {
  startDateTime?: string | null;
  timezoneName?: string | null;
  venue?: { timezone?: string | null } | null;
}

// `timezoneName` is stamped from the creator's browser, so the venue wins.
export const resolveVenueTimeZone = (entity: VenueTimedEntity): string | undefined =>
  entity.venue?.timezone || entity.timezoneName || undefined;

/** Values behind each screen's own `TimeEnum`; a string so they can keep them. */
export type TimeOfDay = 'all' | 'morning' | 'afternoon' | 'evening';

const TIME_OF_DAY_BOUNDS: Record<Exclude<TimeOfDay, 'all'>, [number, number]> = {
  morning: [5, 12],
  afternoon: [12, 17],
  evening: [17, 24],
};

export const isInTimeOfDay = (entity: VenueTimedEntity, timeOfDay: string): boolean => {
  if (timeOfDay === 'all') return true;
  const bounds = TIME_OF_DAY_BOUNDS[timeOfDay as Exclude<TimeOfDay, 'all'>];
  if (!bounds || !entity.startDateTime) return !bounds;

  const hour = getHourInTimezone(entity.startDateTime, resolveVenueTimeZone(entity));
  return hour >= bounds[0] && hour < bounds[1];
};

export const filterByTimeOfDay = <T extends VenueTimedEntity>(items: T[], timeOfDay: string): T[] =>
  timeOfDay === 'all' ? items : items.filter((item) => isInTimeOfDay(item, timeOfDay));

/** yyyy-MM-dd for a picked calendar day, in the viewer's zone. */
export const toCalendarDayKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

/** Does this listing fall on `day` *at its venue*? */
export const isOnVenueDay = (entity: VenueTimedEntity, day: Date): boolean => {
  if (!entity.startDateTime) return false;
  return (
    formatISODateInTimezone(entity.startDateTime, resolveVenueTimeZone(entity)) ===
    toCalendarDayKey(day)
  );
};

export const filterByVenueDay = <T extends VenueTimedEntity>(items: T[], day: Date | null): T[] =>
  day ? items.filter((item) => isOnVenueDay(item, day)) : items;

// Both sides are yyyy-MM-dd, so string comparison is chronological.
export const isOnOrAfterVenueDay = (entity: VenueTimedEntity, day: Date): boolean => {
  if (!entity.startDateTime) return false;
  return (
    formatISODateInTimezone(entity.startDateTime, resolveVenueTimeZone(entity)) >=
    toCalendarDayKey(day)
  );
};

/** For "from this day onward" lists; trims the padding day getVenueDayWindow adds. */
export const filterFromVenueDay = <T extends VenueTimedEntity>(items: T[], day: Date | null): T[] =>
  day ? items.filter((item) => isOnOrAfterVenueDay(item, day)) : items;

// Pads a day each side: the query can't know each venue's zone, so callers narrow per row.
// Only pads backwards for a picked day — with no date the list means "upcoming", and
// nothing upcoming falls before the viewer's midnight today.
export const getVenueDayWindow = ({
  selectedDate,
  forwardDays = 0,
}: {
  selectedDate: Date | null;
  forwardDays?: number;
}): { startDate: string; endDate: string } => {
  const start = selectedDate ? new Date(selectedDate) : new Date();
  start.setHours(0, 0, 0, 0);
  if (selectedDate) start.setDate(start.getDate() - 1);

  const end = selectedDate ? new Date(selectedDate) : new Date();
  end.setDate(end.getDate() + forwardDays + 1);
  end.setHours(23, 59, 59, 999);

  return { startDate: start.toISOString(), endDate: end.toISOString() };
};
